"use client";
import { useCallback, useEffect, useMemo, useState } from "react";
import { PixelText } from "@/components/ui/pixel-text";

export type ItemCategory = "ball" | "heal" | "flee" | "misc";

export type ItemEntry = {
  id: string;
  name: string;
  description: string;
  count: number;
  category: ItemCategory;
};

interface BattleBagProps {
  items: ItemEntry[];
  enabled: boolean;
  onUse: (itemId: string) => void;
  onBack: () => void;
}

const CREAM = "#f5ecd6";
const INK = "#1a1a1a";
const DIM = "#8a8377";

const TABS: { key: ItemCategory; label: string }[] = [
  { key: "ball", label: "Balls" },
  { key: "heal", label: "Heal" },
  { key: "flee", label: "Flee" },
  { key: "misc", label: "Misc" },
];

export function BattleBag({ items, enabled, onUse, onBack }: BattleBagProps) {
  const [tabIndex, setTabIndex] = useState(0);
  const [cursor, setCursor] = useState(0);

  const activeTab = TABS[tabIndex].key;

  const filtered = useMemo(
    () => items.filter((it) => it.category === activeTab),
    [items, activeTab]
  );

  // Reset cursor when tab or filtered list changes (e.g. counts decrement).
  useEffect(() => {
    setCursor(0);
  }, [tabIndex]);

  // Clamp cursor if list shrinks.
  useEffect(() => {
    if (cursor > filtered.length - 1) {
      setCursor(Math.max(0, filtered.length - 1));
    }
  }, [filtered.length, cursor]);

  const isUsable = useCallback((item: ItemEntry) => item.count > 0, []);

  const moveCursor = useCallback(
    (dir: 1 | -1) => {
      if (filtered.length === 0) return;
      let next = cursor;
      for (let step = 0; step < filtered.length; step++) {
        next = (next + dir + filtered.length) % filtered.length;
        if (isUsable(filtered[next])) {
          setCursor(next);
          return;
        }
      }
      // All disabled — leave cursor where it is.
    },
    [cursor, filtered, isUsable]
  );

  const switchTab = useCallback((dir: 1 | -1) => {
    setTabIndex((t) => (t + dir + TABS.length) % TABS.length);
  }, []);

  useEffect(() => {
    if (!enabled) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      const key = e.key;
      const handled = [
        "ArrowUp",
        "ArrowDown",
        "ArrowLeft",
        "ArrowRight",
        "Enter",
        "Tab",
        "z",
        "Z",
        "x",
        "X",
        "q",
        "Q",
        "e",
        "E",
        "Escape",
      ];
      if (handled.includes(key)) {
        e.preventDefault();
      }

      switch (key) {
        case "ArrowUp":
          moveCursor(-1);
          break;
        case "ArrowDown":
          moveCursor(1);
          break;
        case "ArrowLeft":
        case "q":
        case "Q":
          switchTab(-1);
          break;
        case "ArrowRight":
        case "e":
        case "E":
          switchTab(1);
          break;
        case "Tab":
          switchTab(e.shiftKey ? -1 : 1);
          break;
        case "Enter":
        case "z":
        case "Z": {
          const item = filtered[cursor];
          if (item && isUsable(item)) onUse(item.id);
          break;
        }
        case "x":
        case "X":
        case "Escape":
          onBack();
          break;
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [enabled, cursor, filtered, moveCursor, switchTab, isUsable, onUse, onBack]);

  const focusedItem = filtered[cursor];
  const dimmed = !enabled;

  return (
    <div
      className={`flex flex-col gap-3 ${
        dimmed ? "opacity-50 pointer-events-none" : ""
      }`}
      style={{
        backgroundColor: CREAM,
        border: `2px solid ${INK}`,
        padding: "1rem 1.25rem",
        imageRendering: "pixelated",
        minWidth: "32rem",
      }}
      role="dialog"
      aria-label="Bag"
    >
      {/* Tab strip */}
      <div
        className="flex gap-0"
        role="tablist"
        aria-label="Item categories"
      >
        {TABS.map((tab, i) => {
          const active = i === tabIndex;
          return (
            <button
              key={tab.key}
              type="button"
              role="tab"
              aria-selected={active}
              onClick={() => setTabIndex(i)}
              className="font-mono font-bold uppercase tracking-widest text-left"
              style={{
                backgroundColor: active ? INK : CREAM,
                color: active ? CREAM : INK,
                border: `2px solid ${INK}`,
                borderRight: i === TABS.length - 1 ? `2px solid ${INK}` : "none",
                padding: "0.5rem 0.875rem",
                fontSize: "0.85rem",
                lineHeight: 1,
                transition: "none",
                imageRendering: "pixelated",
              }}
            >
              {tab.label}
            </button>
          );
        })}
      </div>

      {/* Body: list + description */}
      <div
        className="grid"
        style={{
          gridTemplateColumns: "1fr 1fr",
          gap: "0.75rem",
          border: `2px solid ${INK}`,
          backgroundColor: CREAM,
        }}
      >
        {/* Scrollable list */}
        <div
          style={{
            maxHeight: "14rem",
            overflowY: "auto",
            borderRight: `2px solid ${INK}`,
          }}
          role="listbox"
          aria-label={`${TABS[tabIndex].label} items`}
        >
          {filtered.length === 0 ? (
            <div
              className="font-mono uppercase tracking-widest"
              style={{
                color: INK,
                padding: "0.75rem",
                fontSize: "0.85rem",
                lineHeight: 1.2,
              }}
            >
              No items in this category.
            </div>
          ) : (
            filtered.map((item, i) => {
              const active = i === cursor;
              const usable = isUsable(item);
              const fg = usable ? INK : DIM;
              return (
                <button
                  key={item.id}
                  type="button"
                  role="option"
                  aria-selected={active}
                  aria-disabled={!usable}
                  disabled={!usable}
                  onMouseEnter={() => {
                    if (usable) setCursor(i);
                  }}
                  onClick={() => {
                    if (usable) onUse(item.id);
                  }}
                  className="relative font-mono font-bold uppercase tracking-widest text-left w-full block"
                  style={{
                    backgroundColor: active && usable ? INK : CREAM,
                    color: active && usable ? CREAM : fg,
                    border: "none",
                    borderBottom: `2px solid ${INK}`,
                    padding: "0.5rem 0.5rem 0.5rem 1.5rem",
                    fontSize: "0.85rem",
                    lineHeight: 1,
                    transition: "none",
                    imageRendering: "pixelated",
                    cursor: usable ? "pointer" : "not-allowed",
                  }}
                >
                  {active && usable && (
                    <span
                      aria-hidden
                      style={{
                        position: "absolute",
                        left: "0.375rem",
                        top: "50%",
                        transform: "translateY(-50%)",
                        color: CREAM,
                        fontSize: "0.85rem",
                        lineHeight: 1,
                      }}
                    >
                      &#9654;
                    </span>
                  )}
                  <span style={{ display: "inline-block" }}>{item.name}</span>
                  <span style={{ float: "right" }}>{`x${item.count}`}</span>
                </button>
              );
            })
          )}
        </div>

        {/* Description pane */}
        <div
          style={{
            padding: "0.75rem",
            minHeight: "6rem",
          }}
          aria-live="polite"
        >
          {focusedItem ? (
            <div className="flex flex-col gap-2">
              <PixelText
                as="div"
                glow="none"
                size="md"
                className="!text-[#1a1a1a] !tracking-normal !normal-case"
              >
                {focusedItem.name}
              </PixelText>
              <div
                className="font-mono"
                style={{
                  color: INK,
                  fontSize: "0.8rem",
                  lineHeight: 1.35,
                  whiteSpace: "normal",
                }}
              >
                {focusedItem.description}
              </div>
            </div>
          ) : (
            <div
              className="font-mono uppercase tracking-widest"
              style={{
                color: DIM,
                fontSize: "0.75rem",
                lineHeight: 1.2,
              }}
            >
              Select an item.
            </div>
          )}
        </div>
      </div>

      {/* Footer hint */}
      <div
        className="font-mono uppercase tracking-widest"
        style={{
          color: INK,
          fontSize: "0.7rem",
          lineHeight: 1,
          borderTop: `2px solid ${INK}`,
          paddingTop: "0.5rem",
        }}
      >
        Z Use &nbsp;&middot;&nbsp; X Back &nbsp;&middot;&nbsp; Q/E Tabs
      </div>
    </div>
  );
}
